import React from "react";
import { Player } from "theoplayer/THEOplayer.chromeless";
import { cx } from "utils/join-classnames";
import secondsToTimecode from "utils/secondsToTimecode";
import { combineTimestamps, formatCombinedTimestamp } from "core/models";
import styles from "./ScrubBar.module.scss";

interface Props {
    vidRef: Player | null;
    size?: "small" | "medium" | "large";
    trim?: [number, number];
    showCurrentTime?: boolean;
    expanded?: boolean;
    selectionTimestamp?: string;
    inEditor?: boolean;
}

const ScrubBarV2: React.FC<Props> = ({
    vidRef,
    size = "medium",
    trim = [0, 0],
    showCurrentTime = false,
    expanded = false,
    selectionTimestamp,
    inEditor,
}) => {
    const [dragging, setDragging] = React.useState(false);
    const [showMouseTimecode, setShowMouseTimecode] = React.useState(false);
    const [mouseTimecode, setMouseTimecode] = React.useState("00:00");
    const [currentTime, setCurrentTime] = React.useState(0);

    const progressRef = React.useRef<HTMLDivElement>(null);
    const thumbRef = React.useRef<HTMLDivElement>(null);
    const trackRef = React.useRef<HTMLDivElement>(null);
    const timecodeRef = React.useRef<HTMLDivElement>(null);

    const [trimStart, trimEnd] = trim;

    const updateProgress = React.useCallback(() => {
        if (!vidRef || !progressRef.current || !trackRef.current) {
            return;
        }
        const trackWidth = trackRef.current.offsetWidth;
        const duration = vidRef.duration - (trimStart + trimEnd);
        const time = Math.max(0, vidRef.currentTime - trimStart);
        const progress = Math.min(1, time / duration);
        progressRef.current.style.width = `${trackWidth * progress}px`;
        if (showCurrentTime) {
            setCurrentTime(vidRef.currentTime);
        }
    }, [vidRef, trimStart, trimEnd, showCurrentTime]);

    const scrubTo = React.useCallback(
        (clientX: number) => {
            if (!vidRef || !trackRef.current) {
                return;
            }
            const trackWidth = trackRef.current.offsetWidth;
            const { left } = trackRef.current.getBoundingClientRect();
            const percent = (clientX - left) / trackWidth;
            const duration = vidRef.duration - (trimStart + trimEnd);
            vidRef.currentTime = trimStart + duration * percent;
            updateProgress();
        },
        [vidRef, trimStart, trimEnd, updateProgress],
    );

    const handleTrackClick = (e: React.MouseEvent) => {
        e.preventDefault();
        scrubTo(e.clientX);
    };

    const handleThumbDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setDragging(true);
    };

    React.useEffect(() => {
        if (!dragging) {
            return;
        }
        const onMove = (e: MouseEvent) => scrubTo(e.clientX);
        const onUp = () => setDragging(false);
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
        return () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
    }, [dragging, scrubTo]);

    React.useEffect(() => {
        if (!showMouseTimecode) {
            return;
        }
        const onMove = (e: MouseEvent) => {
            if (!trackRef.current || !vidRef || !timecodeRef.current) {
                return;
            }
            const trackWidth = trackRef.current.offsetWidth;
            const { left } = trackRef.current.getBoundingClientRect();
            const percent = (e.clientX - left) / trackWidth;
            const duration = vidRef.duration - (trimStart + trimEnd);
            const seconds = duration * percent;
            timecodeRef.current.style.left = `${e.clientX - left}px`;
            setMouseTimecode(secondsToTimecode(seconds));
        };
        document.addEventListener("mousemove", onMove);
        return () => document.removeEventListener("mousemove", onMove);
    }, [showMouseTimecode, vidRef, trimStart, trimEnd]);

    React.useEffect(() => {
        if (!vidRef) {
            return;
        }
        const onTimeUpdate = updateProgress;
        const onPlayPause = updateProgress;
        vidRef.addEventListener("timeupdate", onTimeUpdate);
        vidRef.addEventListener("play", onPlayPause);
        vidRef.addEventListener("pause", onPlayPause);
        window.addEventListener("resize", updateProgress);
        updateProgress();
        return () => {
            vidRef.removeEventListener("timeupdate", onTimeUpdate);
            vidRef.removeEventListener("play", onPlayPause);
            vidRef.removeEventListener("pause", onPlayPause);
            window.removeEventListener("resize", updateProgress);
        };
    }, [vidRef, updateProgress]);

    // Combined timestamp
    const combinedTimestamp = combineTimestamps(
        selectionTimestamp || "00:00:00",
        mouseTimecode,
    );
    const formattedCombinedTimestamp =
        formatCombinedTimestamp(combinedTimestamp);

    return (
        <div
            className={cx(
                styles.scrubbar,
                size === "small" && styles.small,
                expanded && styles["scrubbar--expanded"],
            )}
            ref={trackRef}
            onClick={handleTrackClick}
            onMouseEnter={() => setShowMouseTimecode(true)}
            onMouseLeave={() => setShowMouseTimecode(false)}
            role="presentation"
        >
            <div className={styles.scrubbar__progress} ref={progressRef} />
            <div className={styles.scrubbar__holder}>
                <div
                    className={styles.scrubbar__thumb}
                    ref={thumbRef}
                    onMouseDown={handleThumbDown}
                    role="presentation"
                />
                {showCurrentTime && vidRef && (
                    <div className={styles.currentTime}>
                        {secondsToTimecode(currentTime)}
                    </div>
                )}
            </div>
            <div className={styles.scrubbar__spacer} />
            <div
                className={cx(
                    styles.mousetimecode,
                    inEditor && styles.doubleTimeCode,
                )}
                ref={timecodeRef}
            >
                {showMouseTimecode && mouseTimecode}
                {inEditor && (
                    <>
                        <br />
                        {showMouseTimecode && formattedCombinedTimestamp}
                    </>
                )}
            </div>
        </div>
    );
};

export default ScrubBarV2;
